import React, {useEffect, useState} from 'react';
import {Input, Label, Button, Form, FormGroup} from 'reactstrap';
import './YoutubeTrailer.scss';

const parseYoutubeUrl = url => {
    const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
}

const YoutubeTrailer = ({videoId, value, canEdit = false, onSave, width = '100%', height = '100%', fullscreen = false}) => {
    const [trailerURL, setTrailerURL] = useState('');
    const [videoSrc, setVideoSrc] = useState(`https://www.youtube.com/embed/${videoId}`);
    const [isValidUrl, setIsValidUrl] = useState(false);

    useEffect(() => {
        setTrailerURL(value || '');
        if (value) {
            setVideoSrc(`https://www.youtube.com/embed/${value}`);
            setIsValidUrl(true);
        } else {
            setIsValidUrl(false);
        }
    }, [value]);

    const handleChange = e => {
        const url = e.target.value;
        const parsedUrl = parseYoutubeUrl(url);

        if (parsedUrl) {
            setTrailerURL(parsedUrl);
            setIsValidUrl(true);
            setVideoSrc(`https://www.youtube.com/embed/${parsedUrl}`);
        } else {
            setTrailerURL(url);
            setIsValidUrl(false);
        }
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (isValidUrl) {
            onSave(trailerURL);
            setTrailerURL('');
        }
    };

    const handleCancel = () => {
        setTrailerURL(value || '');
        setVideoSrc(`https://www.youtube.com/embed/${videoId}`);
        setIsValidUrl(false);
    };

    const handleRemove = () => {
        onSave(null);
        setTrailerURL('');
        setIsValidUrl(false);
    };

    if (canEdit) {
        return (
            <div className="youtube-trailer-edit">
                <div className="trailer-preview-box">
                    <div className="ratio ratio-16x9">
                        <iframe
                            src={videoSrc}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Youtube Trailer"
                        />
                    </div>
                </div>

                <Form onSubmit={handleSave} className="trailer-form">
                    <FormGroup>
                        <Label htmlFor="trailer-url" className="form-label">YouTube URL</Label>
                        <div className="input-with-validation">
                            <Input
                                id="trailer-url"
                                type="text"
                                value={trailerURL}
                                onChange={handleChange}
                                placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                                className={isValidUrl ? 'is-valid' : ''}
                            />
                            {isValidUrl && (
                                <span className="validation-icon">
                                    <span className="fas fa-check-circle"/>
                                </span>
                            )}
                        </div>
                        <small className="form-text">
                            <span className="fas fa-info-circle me-1"/>
                            Paste a YouTube URL (supports youtube.com and youtu.be links)
                        </small>
                    </FormGroup>

                    <div className="form-actions">
                        {(videoId || value) && (
                            <Button
                                type="button"
                                color="danger"
                                outline
                                onClick={handleRemove}
                                size="sm"
                                className="remove-btn"
                            >
                                <span className="fas fa-trash me-2"/>
                                Remove
                            </Button>
                        )}
                        <div className="action-buttons">
                            <Button
                                type="button"
                                color="secondary"
                                outline
                                onClick={handleCancel}
                                size="sm"
                            >
                                <span className="fas fa-times me-2"/>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={!isValidUrl}
                                size="sm"
                            >
                                <span className="fas fa-save me-2"/>
                                Save
                            </Button>
                        </div>
                    </div>
                </Form>
            </div>
        );
    }

    // Non-edit mode - just show the trailer
    return (
        <div className={`youtube-trailer-view ${fullscreen ? 'fullscreen' : ''}`}>
            <div className="ratio ratio-16x9">
                <iframe
                    src={videoSrc}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Youtube Trailer"
                />
            </div>
        </div>
    );
};

export default YoutubeTrailer;
